import { ImageScrub } from '@iconik-io/shared-lib/src/react/components/shared/image_scrub/image_scrub';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { findBestKeyframe } from '@iconik-io/shared-lib/src/utils/find_best_keyframe';
import { isLiveAsset } from '@iconik-io/shared-lib/src/utils/is_live_asset';
import {
  MEDIA_TYPE_SUBCLIP,
  MEDIA_TYPE_VIDEO,
} from '@iconik-io/shared-lib/src/utils/media_type';

import { getSearchItemMediaType } from '../../utils/media_type';
import { parseObjectIconInfo } from '../../utils/parse_object_icon_info';
import { ObjectIcon } from '../object_icon/object_icon';
import { ObjectIconThumbnail } from '../object_icon_thumbnail/object_icon_thumbnail';
import { SearchWarningSign } from '../search_warning_sign/search_warning_sign';
import { KEYFRAME_MAP } from './constants';

import styles from './keyframes.module.css';

type Props = {
  searchItem: SearchItemEntity;
  highResolution?: boolean;
  imageScrub?: 'auto' | 'off';
  size?: number;
  isThumbnail?: boolean;
  showWarningIcon?: boolean;
};

function KeyframesComponent({
  searchItem,
  isThumbnail,
  imageScrub = 'auto',
  size = 175,
}: Props) {
  const keyframes = searchItem.keyframes ?? undefined;
  // While the asset is live its keyframes are still GROWING; show them anyway so
  // the card is not stuck on the placeholder for the length of the ingest. The
  // scrub map stays gated: a half-written sprite scrubs to blank frames.
  const isLive = isLiveAsset(searchItem);
  const keyframe = findBestKeyframe(keyframes, isLive);

  const keyframeMap = keyframes?.find(({ type }) => type === KEYFRAME_MAP);
  let isKeyframeMap =
    keyframeMap?.url && keyframeMap.status === 'CLOSED' && imageScrub !== 'off';

  const isKeyframe =
    keyframe?.url &&
    (keyframe.status === 'CLOSED' || (isLive && keyframe.status === 'GROWING'));

  const mediaType = getSearchItemMediaType(searchItem);
  if (
    mediaType !== MEDIA_TYPE_VIDEO &&
    mediaType !== MEDIA_TYPE_SUBCLIP &&
    searchItem.format !== 'GIF'
  ) {
    isKeyframeMap = false;
  }

  if (isKeyframeMap && keyframeMap && keyframe) {
    return (
      <ImageScrub
        keyframeMapSrc={keyframeMap.url ?? undefined}
        keyframePosterSrc={keyframe.url ?? undefined}
        objectTitle={searchItem.title}
        className={isThumbnail ? styles.small_thumbnail : undefined}
        frameWidth={keyframeMap.resolution?.width}
      />
    );
  }

  if (!isKeyframeMap && isKeyframe && keyframe) {
    return (
      <div
        className={isThumbnail ? styles.small_thumbnail : styles.big_thumbnail}
        style={{ backgroundImage: `url('${keyframe.url}')` }}
        role="img"
        aria-label={`Keyframe preview for ${searchItem.title}`}
      />
    );
  }
  const objectIconInfo = parseObjectIconInfo(searchItem);
  if (isThumbnail) {
    return <ObjectIconThumbnail objectIconInfo={objectIconInfo} />;
  }

  return (
    <ObjectIcon
      info={objectIconInfo}
      size={size}
      className={styles.placeholder}
    />
  );
}

export function Keyframes(props: Props) {
  const { searchItem, showWarningIcon } = props;
  return (
    <>
      <KeyframesComponent {...props} />
      {showWarningIcon && searchItem.warning ? (
        <SearchWarningSign item={searchItem} />
      ) : null}
    </>
  );
}
